import { http } from '@/plugins/http'
import { mapGetters } from 'vuex'
import { getHeaders } from '@/utils/api'
import { sortObjectByProperty, sortResourcesByProperty } from '@/utils/helpers/ObjectSorting'

/*
 * Helper apps to load multiple pages of data without using the store
 */
export const aggregateMixins = {
  data () {
    return {
      instanceUrl: this.$store.getters.getActiveInstance.url,
      headers: getHeaders(this.$store.getters.getToken),
      pageSize: 50
    }
  },

  computed: {
    ...mapGetters({
      getVendorById: 'getVendorById'
    })
  },

  methods: {
    /*
     * Post to an endpoint, options should be an object of additional parameters
     */
    updateData (endpoint, payload, options) {
      // Add addt'l options if any
      const setPageSize = false
      let url = this.urlBuilder(this.instanceUrl + endpoint, options, setPageSize)

      // Define method
      let method
      if (options && options.method) {
        method = options.method
      } else if (payload.id) {
        method = 'patch'
      } else {
        method = 'post'
      }

      // Update data
      return http[method](url.toString(), payload, { headers: this.headers })
        .then(response => {
          if (response && response.status === (method === 'post' ? 201 : 200)) {
            return response
          } else {
            return false
          }
        })
        .catch(error => {
          console.log(error)
          return error
        })
    },

    /*
     * Get a single page, options should be an object of additional parameters
     */
    getPage (endpoint, options) {
      // Add addt'l options if any
      let url = this.urlBuilder(this.instanceUrl + endpoint, options)

      // Get page
      return http.get(url.toString(), { headers: this.headers })
        .then(response => {
          if (response && response.status === 200) {
            return response
          } else {
            return false
          }
        })
        .catch(error => {
          console.log(error)
          return error
        })
    },

    /*
     * Post to an endpoint, options should be an object of additional parameters
     */
    postData (endpoint, payload, options) {
      // Add addt'l options if any
      const setPageSize = false
      let url = this.urlBuilder(this.instanceUrl + endpoint, options, setPageSize)

      // Get page
      return http.post(url.toString(), payload, { headers: this.headers })
        .then(response => {
          if (response && response.status === 201) {
            return response
          } else {
            return false
          }
        })
        .catch(error => {
          console.log(error)
          return error
        })
    },

    /*
     * Get multiple pages, options should be an object of additional parameters
     * Examples:  this.getAllPages('/accounts')
     *            this.getAllPages('lines/' + type , { export__in: 1})
     *            this.getAllPages('http://localhost:8000/checks?export__isnull=true', { jwt: [token] })
     */
    getAllPages (endpoint = '/', options = null, paginate = false) {
      return new Promise((resolve, reject) => {
        // Get at least page one.
        let pages = 1
        this.getPage(endpoint, options)
          .then((response) => {
            if (!response) return reject('No Data')

            // Not authorized
            if (response.status === 400 || response.status === 403) {
              return reject(response)

            // 500 or other unknown error
            } else if (response.status !== 200) {
              return reject(response)

            // This means this is the last page.  Calc pages appropriately
            } else if (response.data.previous && !response.data.next) {
              pages = parseInt(response.data.previous.split('page=')[1], 10) + 1
              response.data.pages = pages // add pages property

            // This means more than one page of data. We'll get pages by queueing up all requests.
            } else if (response.data.next) {
              pages = Math.ceil((parseInt(response.data.count, 10) / response.data.results.length))
              response.data.pages = pages // add pages property

            // This means only that one page was found.
            } else if (response.data) {
              response.data.pages = pages // add pages property
              return resolve(response)

            // Shouldn't be here, but we'll put something here as a placeholder
            } else {
              console.log('Unknown response')
              return resolve(response)
            }

            // Return data if we want only the current page
            if (paginate) {
              return resolve(response)
            }

            // If here, we want to send all the results at once.  Useful for setup codes, e.g. productions
            // Now build up number of additional pages to request, but skip page one since we already have it
            const queue = []
            for (let i = 2; i <= pages; i++) {
              const curOptions = Object.assign({ page: i }, options)
              const request = this.getPage(endpoint, curOptions)
              queue.push(request)
            }

            // Now get remaining pages, but make sure to queue properly
            Promise.all(queue)
              .then((responses) => {
                // Now add to original record:
                responses.map((res) => {
                  if (res && res.status === 200 && res.data) {
                    res.data.results.map((record) => response.data.results.push(record))
                  }
                })

                return resolve(response)
              })
              .catch(error => {
                console.log(error)
                return reject(error)
              })
          })
      })
    },

    // Helper to build a url with potentially two sets of querystrings
    urlBuilder (url = null, options = null, setPageSize = true) {
      if (!url) return false

      let urlObject = new URL(url)
      if (setPageSize) {
        urlObject.searchParams.set('page_size', this.pageSize || 50)
      }

      if (options && typeof options === 'object') {
        Object.keys(options).map(key => {
          urlObject.searchParams.set(key, options[key])
        })
      }

      // console.log(urlObject.toString())
      return urlObject.toString()
    },

    /*
     * Helper to get Aggregate data.  Type can be a single transaction type or array of transaction types
     * Examples:  this.getTransactionLines('ap')
     *            this.getTransactionLines(['ap', 'je', 'pc'], {"account__in": 'a92544a4-71cf-4a7e-adc8-a789ca052d90' })
     */
    getTransactionLines (type = null, options = null) {
      return new Promise((resolve, reject) => {
        const allowedTypes = [ 'ap', 'je', 'pc', 'po', 'ar', 'pp', 'bp' ]
        let transTypes = []

        if (!type) {
          return reject('No transaction types specified')
        } else if (Array.isArray(type)) {
          transTypes = type
        } else if (typeof type === 'string') {
          transTypes = [ type ]
        } else {
          return reject('No transaction types specified')
        }

        let queue = transTypes.map((type) => {
          if (allowedTypes.includes(type)) {
            return this.getAllPages('lines/' + type + '/aggregate', options)
          }
        })

        Promise.all(queue)
          .then((responses) => {
            if (responses) {
              // Merge all data
              let currencyCode
              const results = [].concat(...responses.map(res => {
                if (res && res.status === 200 && res.data) {
                  if (!currencyCode) currencyCode = res.data.currency_code
                  return res.data.results
                }
              }))

              const count = responses.reduce((acc, cur) => {
                if (cur && cur.status === 200 && cur.data) return acc + parseInt(cur.data.count)
              }, 0)

              const aggregate = responses.reduce((acc, cur) => {
                if (cur && cur.status === 200 && cur.data) return acc + parseFloat(cur.data.aggregate)
              }, 0)

              const finalResult = { count, aggregate, results, currency_code: currencyCode }
              // console.log(finalResult)
              return resolve(finalResult)
            }
          })
          .catch(error => {
            console.log(error)
            return reject(error)
          })
      })
    },

    /*
     * Helper to get Checks/Manual Checks data.  Type can be a single transaction type or array of transaction types
     * Examples:  this.getTransactionLines('ap')
     *            this.getTransactionLines(['ap', 'je', 'pc'], {"account__in": 'a92544a4-71cf-4a7e-adc8-a789ca052d90' })
     */
    getPositivePayIds (type = null, bank = null, exportId = null) {
      return new Promise((resolve, reject) => {
        // User will be defining types of postive pay transactions, e.g. checks and/or manual checks
        let transTypes = []
        if (!type) {
          return reject('No transaction types specified')
        } else if (!bank || !bank.hasOwnProperty('id')) {
          return reject('No bank specified')
        } else if (Array.isArray(type)) {
          transTypes = type
        } else if (typeof type === 'string') {
          transTypes = [ type ]
        } else {
          return reject('No transaction types specified')
        }

        // Prepare options to get data
        let defaultOptions = {
          batch__bank: bank.id || '',
          status__in: 'POSTED,CLOSED,VOIDED',
          nest_lines: false
        }

        if (exportId) {
          defaultOptions.export = exportId
        } else {
          defaultOptions.export__isnull = 'TRUE'
        }

        // Build Promises for each transaction type.  Voids are a special case that can happen after the fact.
        let checkPromise = Promise.resolve(null)
        let manualCheckPromise = Promise.resolve(null)
        let voidsPromise = Promise.resolve(null)

        for (const i in transTypes) {
          let options = Object.assign({}, defaultOptions)
          switch (transTypes[i].toLowerCase()) {
            case 'check':
              checkPromise = this.getAllPages('checks', options)

              let voidOptions = Object.assign({}, defaultOptions)
              voidOptions.status__in = 'POSTED,CLOSED'
              voidOptions.source_code__code = 'VX'
              voidsPromise = this.getAllPages('jes', voidOptions)

              break

            case 'check_manual':
              options.payment_type = 'CHECK_MANUAL'
              manualCheckPromise = this.getAllPages('aps', options)

              break

            default:
          }
        }

        // Transaction Placeholders to place promised data
        let checks = []
        let manualChecks = []
        let voidJournals = []
        let voidChecks = []

        // Get (manual) check data from API
        Promise.all([ checkPromise, manualCheckPromise, voidsPromise ])
          .then(([ resultChecks, resultManualChecks, resultVoids ]) => {
            checks = sortObjectByProperty((resultChecks.data || {}).results || [], 'number')
            manualChecks = sortObjectByProperty((resultManualChecks.data || {}).results || [], 'number')
            voidJournals = ((resultVoids.data || {}).results || []).filter(v => v.description.match(/^Voided Check/))

            // Handle no data
            if (!checks.length && !manualChecks.length && !voidJournals.length) return reject('No New Data to Export')

            // Lookup original check details for voids
            const voidedCheckIds = voidJournals.map(v => v.original_trx_number)
            return (voidedCheckIds && voidedCheckIds.length) ? this.getAllPages('checks', { number__in: voidedCheckIds.join(','), batch__bank: bank.id || '', export__isnull: false }) : {}
          })
          .then((originalChecks) => {
            // console.log(originalChecks)
            if (originalChecks && originalChecks.data && !exportId) {
              voidChecks = sortObjectByProperty((originalChecks.data || {}).results || [], 'number')
            }

            // Lookup vendor_name for manual checks
            const manualVendors = manualChecks.map(trans => trans.vendor)
            return (manualVendors && manualVendors.length) ? this.$store.dispatch('fetchVendorsWithQuery', { id__in: manualVendors }) : true
          })
          .then(() => {
            // Add newly fetched vendor names to output
            manualChecks = manualChecks.map(trans => {
              trans.vendor_name = this.getVendorById(trans.vendor).payee_name
              return trans
            })

            // Get Check IDs to mark as exported
            const checkIds = checks.map(check => check.id)
            const manualCheckIds = manualChecks.map(manual => manual.id)
            const voidIds = voidJournals.map(voidCheck => voidCheck.id)
            const exportData = {
              bank: bank.id,
              papercheck: checkIds,
              accountspayable: manualCheckIds,
              pettycash: [],
              journalentry: voidIds
            }

            // Mark them as exported and save export ID
            if (exportId) {
              return this.getPage('exports/' + exportId)
            } else {
              // Mark as exported
              // console.log('Marking Exports', exportData)
              return this.postData('exports', exportData)
            }
          })
          .then(exp => {
            // Now add our transactions to save on API calls
            if (exp && exp.data) {
              exp.data.papercheckTrx = checks
              exp.data.accountsPayableTrx = manualChecks
              exp.data.voidTrx = voidChecks
              return resolve(exp.data)
            } else {
              return reject(exp)
            }
          })
          .catch(error => {
            console.log(error)
            return reject(error)
          })
      })
    },

    /*
     * Helper to get EFT Payment data.  Note that this is mutually exclusive from Positive Pay.
     */
    async getExportableACH (bank = null, exportId = null, ids = []) {
      // Prepare options to get data
      let query = {
        batch__bank: bank.id || '',
        payment_type: 'EFT',
        status__in: 'POSTED,CLOSED'
      }
      if (ids && ids.length) {
        query.id__in = ids.join(',')
      }
      if (exportId) {
        query.export = exportId
      } else {
        query.export__isnull = 'TRUE'
      }

      let eftRequest = await this.resources.AccountsPayableResource.all({ query, resolveRelated: true })
      return sortResourcesByProperty(eftRequest, 'number', 1)
    },

    // Get AP IDs to mark as exported
    async markExportedACH (ApResources = null, bank = null, paymentDates = []) {
      if (!bank || !ApResources) return
      const eftIds = ApResources.map((resource) => resource.id)
      const exportData = {
        bank: bank.id,
        accountspayable: eftIds,
        papercheck: [],
        pettycash: [],
        journalentry: [],
        paymentDates
      }

      let newExport = await this.postData('exports', exportData)

      // Now add our transactions to save on API calls
      if (newExport && newExport.data) {
        newExport.data.accountsPayableTrx = eftIds
        return newExport.data
      } else {
        return undefined
      }
    },

    /*
     * Helper to get Payroll DD Payment data.  Note that this is mutually exclusive from Positive Pay.
     * returns AccountsPayableResources[]
     */
    async getExportableDirectDeposits (bankId = null) {
      let apIds
      // Prepare options to get data
      let exportQuery = {
        export_type: 'PR_NACHA_DD',
        payment_type: 'DIRECT_DEPOSIT',
        status__in: 'POSTED,CLOSED',
        batch__bank: bankId || '',
        nest_lines: false,
        deposit_account__isnull: false,
        page_size: 500
      }

      // let ddRequest = await this.resources.AccountsPayableResource.wrap('/unexported_for_type').get({ exportQuery, resolveRelated: true })
      let ddRequest = this.resources.AccountsPayableResource.wrap('/unexported_for_type')
      let ddResponse = await ddRequest.get({ params: exportQuery })
      if (ddResponse && ddResponse.data.count && ddResponse.data.count > 0) {
        apIds = ddResponse.data.results.map((aps) => aps.id).sort()
      } else {
        return
      }

      let apQuery = {
        nest_lines: false,
        id__in: apIds.join(',')
      }

      let apResources = await this.resources.AccountsPayableResource.all({ query: apQuery })
      return apResources
    },

    async getExportableDirectDepositsSQ (bank = null, checkDateLimit = null) {
      if (!bank) bank = this.selectedCodes.bank
      if (!checkDateLimit) bank = this.checkDateLimit

      let apIds
      // Prepare options to get data
      this.selectedCodes.bank_id = bank
      this.selectedCodes.check_date_end = checkDateLimit
      this.reportOptions.transactionStatuses = [ 'POSTED', 'CLOSED' ]
      this.includedTypes = [ 'PR_NACHA_DD' ]

      const ddResponse = await this.getReportData('payrollinterface')

      if (ddResponse && ddResponse.data[0] && ddResponse.data[0].length > 0) {
        apIds = ddResponse.data[0].map((aps) => aps.id).sort()
      } else {
        return
      }

      let apQuery = {
        nest_lines: false,
        id__in: apIds.join(',')
      }

      let apResources = await this.resources.AccountsPayableResource.all({ query: apQuery })
      return apResources
    },

    // Get AP IDs to mark as exported
    async markExportedDd (ApResources = null, bank = null) {
      if (!bank || !ApResources) return
      const ddIds = ApResources.map((resource) => resource.id)
      const exportData = {
        receipt: null,
        bank: bank.id,
        meta_data: null,
        type: 'PR_NACHA_DD',
        accountspayable: ddIds,
        accountsreceivable: [],
        billpayments: [],
        journalentries: [],
        paperchecks: [],
        payrolls: [],
        pettycash: [],
        prepayments: []
      }

      let newExport = await this.postData('typedexports', exportData)

      // Now add our transactions to save on API calls
      if (newExport && newExport.data) {
        newExport.data.accountsPayableTrx = ddIds
        return newExport.data
      } else {
        return undefined
      }
    },

    /*
     * Helper to get Unexported Payrolls to export.
     * returns CustomPayrollResources[]
     */
    async getUnexportedClientInterfaces () {
      let prIds
      // Prepare options to get data
      let exportQuery = {
        export_type: 'PR_CLXP_PB',
        status__in: 'POSTED,CLOSED',
        date__gte: '2023-04-23',
        page_size: 50
      }

      let request = this.resources.CustomPayrollResource.wrap('/unexported_for_type')
      let response = await request.get({ params: exportQuery })
      if (response && response.data.count && response.data.count > 0) {
        prIds = response.data.results.map((prs) => prs.id).sort()
      } else {
        return
      }

      let prQuery = {
        nest_lines: false,
        id__in: prIds.join(',')
      }

      const prResources = await this.resources.CustomPayrollResource.all({ query: prQuery })
      return prResources
    },

    // Get PR IDs to mark as exported
    async markExportedClientInterfaces (prResources = null, bankId = null) {
      if (!bankId || !prResources) return
      const prIds = prResources.map((resource) => resource.id)
      const exportData = {
        receipt: null,
        bank: bankId,
        meta_data: null,
        type: 'PR_CLXP_PB',
        accountspayable: [],
        accountsreceivable: [],
        billpayments: [],
        journalentries: [],
        paperchecks: [],
        payrolls: prIds,
        pettycash: [],
        prepayments: []
      }

      let newExport = await this.postData('typedexports', exportData)

      // return the new export
      if (newExport && newExport.data) {
        return newExport.data
      } else {
        return undefined
      }
    }
  }
}
